/**
 * StageIcon — renders a bank SVG by iconId.
 *
 * Elements that have a StagePlotPro PNG asset (STAGE_IMAGE_MAP) render that
 * grayscale photo-render as an <img>. Elements WITHOUT a PNG fall back to the
 * bank SVG; those are rendered in a neutral grayscale (STAGE_SVG_GRAY) so they
 * stay visually consistent with the grayscale PNG assets — the category /
 * element `color` is intentionally ignored for SVG-drawn icons.
 */
import { STAGE_ICON_BANK_A } from './stageIconBankA';
import { STAGE_ICON_BANK_B } from './stageIconBankB';

const BANK: Record<string, string> = { ...STAGE_ICON_BANK_A, ...STAGE_ICON_BANK_B };

export const STAGE_ICON_BANK = BANK;

/**
 * Neutral grayscale used for every SVG-drawn stage element (icons without a
 * StagePlotPro PNG). The PNG photo-renders are native grayscale and cannot be
 * recoloured, so the remaining vector glyphs use this same neutral gray instead
 * of the category colour, keeping the whole plot monochrome.
 */
export const STAGE_SVG_GRAY = '#444444';

/**
 * iconId → real StagePlotPro PNG asset (grayscale photo-render).
 *
 * Composite elements (drumkit / risers / spd) are deliberately NOT here —
 * they keep their custom SVG rendering. For every other iconId that has a
 * matching StagePlotPro asset, StageIcon renders the <img> instead of the
 * bank SVG. iconIds with no clear asset stay on their existing SVG (fallback),
 * and saved plots are unaffected (iconIds are never renamed).
 */
export const STAGE_IMAGE_MAP: Record<string, string> = {
  // ===== DRUMS =====
  kick: '/stageplot-assets/drum/kick.png',
  snare: '/stageplot-assets/drum/snare.png',
  hihat: '/stageplot-assets/drum/hihat.png',
  racktom: '/stageplot-assets/drum/tom1.png',
  floortom: '/stageplot-assets/drum/floortom.png',
  ride: '/stageplot-assets/drum/ride.png',
  crash: '/stageplot-assets/drum/crash.png',
  cymbal: '/stageplot-assets/drum/cymbal.png',
  stool: '/stageplot-assets/drum/throne.png',
  // Drum carpet — a real StagePlotPro top-down mat PNG (aspect 152/129). Rendered
  // with object-fit: contain, so in a square (2×2 m) box it is fitted by width
  // and leaves a small letterbox top/bottom (the image is never stretched).
  'drum-mat': '/stageplot-assets/drum/drummat.png',
  // Stand-free TOP-DOWN cymbal: the 'cymbal' photo above cropped to its disc
  // (46x46 out of 46x63) — the stand legs are simply not in the frame.
  'cymbal-top': '/stageplot-assets/drum/cymbal-top.png',

  // ===== PERCUSSION =====
  bongos: '/stageplot-assets/percussion/Bongos.png',
  congas: '/stageplot-assets/percussion/conga_double.png',
  timbales: '/stageplot-assets/percussion/timbale.png',
  gong: '/stageplot-assets/percussion/gong.png',
  tambourine: '/stageplot-assets/table/Tambourine.png',
  triangle: '/stageplot-assets/percussion/triangle.png',
  timpani: '/stageplot-assets/percussion/timpani1.png',

  // ===== BASS =====
  'bass-guitar': '/stageplot-assets/bass/P_Bass.png',
  'upright-bass': '/stageplot-assets/string/stringbass.png',
  'bass-amp': '/stageplot-assets/bass/BassAmp.png',
  'bass-cab': '/stageplot-assets/bass/fourtens.png',
  'bass-di': '/stageplot-assets/device/DI.png',

  // ===== GUITAR =====
  'e-guitar': '/stageplot-assets/egt/electricguitar.png',
  'a-guitar': '/stageplot-assets/agt/acousticguitar.png',
  'guitar-amp': '/stageplot-assets/egt/FenderAmp.png',
  'guitar-cab': '/stageplot-assets/egt/MarshallCabs.png',
  'guitar-di': '/stageplot-assets/device/DI.png',
  // Empty guitar stands (gt_stand folder).
  'guitar-stand': '/stageplot-assets/gt_stand/guitarstand.png',
  'guitar-stand-double': '/stageplot-assets/gt_stand/doublestand.png',

  // ===== HORNS =====
  trumpet: '/stageplot-assets/wind/trumpet.png',
  sax: '/stageplot-assets/saxphone/sax.png',
  trombone: '/stageplot-assets/wind/trombone.png',
  frenchhorn: '/stageplot-assets/wind/FrenchHorn.png',
  flute: '/stageplot-assets/string/flute.png',
  clarinet: '/stageplot-assets/wind/clarinet.png',

  // ===== STRINGS =====
  violin: '/stageplot-assets/string/Violin.png',
  viola: '/stageplot-assets/string/viola.png',
  cello: '/stageplot-assets/string/cello.png',
  doublebass: '/stageplot-assets/string/stringbass.png',
  harp: '/stageplot-assets/pluck/harp.png',

  // ===== KEYS =====
  'stage-piano': '/stageplot-assets/keyborad/NordStage88.png',
  'grand-piano': '/stageplot-assets/piano/grandpiano.png',
  'upright-piano': '/stageplot-assets/piano/upright.png',
  synth: '/stageplot-assets/keyborad/voyager.png',
  organ: '/stageplot-assets/keyborad/HammondB3.png',
  keytar: '/stageplot-assets/artist/keytar.png',
  keystand: '/stageplot-assets/keyborad/keystand.png',
  'keystand-apex': '/stageplot-assets/keyborad/ApexStand.png',

  // ===== VOCALS =====
  'vocal-mic': '/stageplot-assets/mic/wired_mic.png',
  condenser: '/stageplot-assets/mic_stand/NeumannU87.png',
  singer: '/stageplot-assets/singer/singer.png',
  iem: '/stageplot-assets/monitor/in_ear.png',

  // ===== STAGE =====
  wedge: '/stageplot-assets/monitor/Wedge.png',
  sidefill: '/stageplot-assets/speaker/sidefill.png',
  monitor: '/stageplot-assets/device/mixer.png',
  sub: '/stageplot-assets/speaker/Subwoofer.png',
  linearray: '/stageplot-assets/speaker/linearray.png',
  paspeaker: '/stageplot-assets/speaker/speakwstand.png',
  outlet: '/stageplot-assets/power/electric.png',
  distro: '/stageplot-assets/equipment/FloorBox.png',
  truss: '/stageplot-assets/equipment/truss.png',
  micstand: '/stageplot-assets/mic_stand/stand_no-mic.png',
  // Mic-stand variants — distinct StagePlotPro assets (thin & tall). Left/right
  // instrument booms are exposed separately because the two photos differ; the
  // editor can also mirror any element with flipX if a mirrored look is wanted.
  'mic-stand-straight': '/stageplot-assets/mic_stand/standstraight_no-mic.png',
  'mic-stand-vocal': '/stageplot-assets/mic_stand/Straight_Vocal.png',
  'mic-stand-boom': '/stageplot-assets/mic_stand/Boom57.png',
  'mic-drum-overhead': '/stageplot-assets/mic_stand/drumoverhead.png',
  'mic-instrument-left': '/stageplot-assets/mic_stand/Left_Instrument.png',
  'mic-instrument-right': '/stageplot-assets/mic_stand/Right_Instrument.png',
  // Racks / flight cases (equipment folder). Mirror variants (RackL/R,
  // flightcaseL/R) are not mapped — the editor's flipX covers that need.
  rack: '/stageplot-assets/equipment/Rack.png',
  flightcase: '/stageplot-assets/equipment/flightcase.png',
  dibox: '/stageplot-assets/device/DI.png',
  chair: '/stageplot-assets/seat/chair.png',

  // ===== TECH =====
  laptop: '/stageplot-assets/device/Laptop.png',
  'drum-machine': '/stageplot-assets/synth/DrumMachine.png',
  mpc: '/stageplot-assets/device/sampler.png',
  'midi-keyboard': '/stageplot-assets/keyborad/NovationMKII.png',
  tablet: '/stageplot-assets/device/ipad.png',
};

export interface StageIconProps {
  iconId: string;
  color: string;
  /** Display pixel size of the square box. */
  size?: number;
  className?: string;
  title?: string;
}

export function StageIcon({ iconId, color: _color, size = 48, className, title }: StageIconProps) {
  // Composite elements render via their own component on-stage; in the
  // palette we show a compact glyph so the entry is still recognisable.
  if (iconId === 'drumkit') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        className={className}
        style={{ color: STAGE_SVG_GRAY }}
        role="img"
        aria-label={title ?? 'Drum Kit'}
      >
        <circle cx="32" cy="44" r="15" fill="currentColor" stroke="#0004" strokeWidth="2" />
        <circle cx="32" cy="44" r="11" fill="#0001" />
        <ellipse cx="22" cy="30" rx="7" ry="5" fill="none" stroke="currentColor" strokeWidth="2.5" />
        <ellipse cx="44" cy="26" rx="8" ry="5" fill="none" stroke="currentColor" strokeWidth="2.5" />
        <path d="M10 14 H22 M42 12 H54" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  }
  if (iconId === 'risers') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        className={className}
        style={{ color: STAGE_SVG_GRAY }}
        role="img"
        aria-label={title ?? 'Risers'}
      >
        <rect x="8" y="26" width="14" height="22" rx="2" fill="currentColor" stroke="#0004" strokeWidth="2" />
        <rect x="25" y="22" width="14" height="26" rx="2" fill="currentColor" stroke="#0004" strokeWidth="2" />
        <rect x="42" y="26" width="14" height="22" rx="2" fill="currentColor" stroke="#0004" strokeWidth="2" />
        <rect x="8" y="22" width="48" height="4" rx="2" fill="#0003" />
      </svg>
    );
  }
  if (iconId === 'spd') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        className={className}
        style={{ color: STAGE_SVG_GRAY }}
        role="img"
        aria-label={title ?? 'SPD Pad'}
      >
        <line x1="32" y1="60" x2="32" y2="34" stroke="#000" strokeWidth="2" />
        <path d="M18 22 L46 22 L50 40 L14 40 Z" fill="currentColor" stroke="#0004" strokeWidth="2" />
        <ellipse cx="32" cy="22" rx="14" ry="3" fill="#fff" fillOpacity="0.85" />
        <circle cx="26" cy="31" r="3" fill="#1A1A1A" />
        <circle cx="38" cy="31" r="3" fill="#1A1A1A" />
        <circle cx="26" cy="37" r="3" fill="#1A1A1A" />
        <circle cx="38" cy="37" r="3" fill="#1A1A1A" />
      </svg>
    );
  }
  const asset = STAGE_IMAGE_MAP[iconId];
  if (asset) {
    // Real StagePlotPro photo-render. Rotation and flipX are applied by the
    // element's wrapper (parent) in the canvas; here we only fit the PNG into
    // the square box. The image is grayscale, so `color` no longer applies.
    return (
      <img
        src={asset}
        alt={title ?? iconId}
        width={size}
        height={size}
        className={className}
        draggable={false}
        style={{ objectFit: 'contain', display: 'block' }}
      />
    );
  }
  const body = BANK[iconId];
  // Strip the icon's own <svg> root so it nests cleanly inside our wrapper
  // (a nested <svg> without explicit width/height often fails to render).
  const inner = body
    ? body.replace(/^\s*<svg[^>]*>/i, '').replace(/<\/svg>\s*$/i, '')
    : '';
  if (!inner) {
    // Fallback: a neutral placeholder box.
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        className={className}
        style={{ color: STAGE_SVG_GRAY }}
        role="img"
        aria-label={title ?? iconId}
      >
        <rect x="12" y="12" width="40" height="40" rx="6" fill="currentColor" opacity="0.5" />
      </svg>
    );
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      preserveAspectRatio="xMidYMid meet"
      className={className}
      style={{ color: STAGE_SVG_GRAY }}
      role="img"
      aria-label={title ?? iconId}
      dangerouslySetInnerHTML={{ __html: inner }}
    />
  );
}

// hasStageIcon REMOVED (audit 2026-10-24 [DEAD]): `iconId in BANK` inlined nowhere —
// callers read the BANK map directly.
